import { create } from 'zustand';
import { isUnauthorizedError } from '@/requestErrorConfig';
import { outLogin, currentUser as queryCurrentUser } from '@/services/api';
import type { CurrentUser as ApiCurrentUser } from '@/services/generated/model';
import {
  submitVerifiedLogin,
  type VerifiedLoginParams,
  type VerifiedLoginResult,
} from '@/services/loginVerification';
import { onSessionReset, resetSession, sessionRevision } from '@/services/session';
import { browserStorage } from '@/utils/browserStorage';

export type CurrentUser = ApiCurrentUser & {
  id: string;
  name: string;
  access: 'admin' | 'user';
};

export type AuthStatus = 'initializing' | 'authenticated' | 'anonymous' | 'error';

type AuthState = {
  currentUser: CurrentUser | null;
  error: Error | null;
  status: AuthStatus;
  initialize: () => Promise<void>;
  login: (
    params: VerifiedLoginParams,
  ) => Promise<VerifiedLoginResult | { status: 'stale' }>;
  cancelLogin: () => void;
  logout: () => Promise<void>;
  refreshCurrentUser: () => Promise<void>;
};

let initializePromise: Promise<void> | undefined;

type LoginAttempt = {
  id: symbol;
  controller: AbortController;
};

let activeLoginAttempt: LoginAttempt | undefined;

function cancelActiveLoginAttempt() {
  const attempt = activeLoginAttempt;
  activeLoginAttempt = undefined;
  attempt?.controller.abort();
}

function isActiveLoginAttempt(attempt: LoginAttempt) {
  return activeLoginAttempt?.id === attempt.id && !attempt.controller.signal.aborted;
}

function normalizeCurrentUser(user?: ApiCurrentUser): CurrentUser | null {
  if (!user || user.isLogin === false) {
    return null;
  }

  return {
    ...user,
    id: user.userid ?? user.email ?? user.name ?? 'current-user',
    name: user.name ?? 'User',
    access: user.access === 'admin' ? 'admin' : 'user',
    permissions: [...user.permissions],
  };
}

function normalizeError(error: unknown) {
  return error instanceof Error ? error : new Error('Unable to load current user');
}

function clearLocalSession() {
  resetSession();
}

export const useAuthStore = create<AuthState>((set, get) => ({
  refreshCurrentUser: async () => {
    const revision = sessionRevision();
    const id = get().currentUser?.id;
    if (!id) return;
    let response: Awaited<ReturnType<typeof queryCurrentUser>>;
    try {
      response = await queryCurrentUser({ skipErrorHandler: true });
    } catch (error) {
      if (
        revision === sessionRevision() &&
        isUnauthorizedError(error) &&
        get().currentUser?.id === id
      ) {
        clearLocalSession();
        set({ currentUser: null, error: null, status: 'anonymous' });
      }
      throw error;
    }
    if (revision !== sessionRevision() || get().currentUser?.id !== id) return;
    const user = normalizeCurrentUser(response.data);
    if (!user || user.id !== id) {
      clearLocalSession();
      set({ currentUser: null, error: null, status: 'anonymous' });
      throw new Error(
        '登录状态已变化，请重新登录。 / Session changed; please sign in again.',
      );
    }
    set({ currentUser: user, error: null, status: 'authenticated' });
  },
  currentUser: null,
  error: null,
  status: 'initializing',
  initialize: () => {
    if (get().status === 'authenticated' || get().status === 'anonymous') {
      return Promise.resolve();
    }

    if (!initializePromise) {
      const revision = sessionRevision();
      if (get().status !== 'initializing' || get().error) {
        set({ error: null, status: 'initializing' });
      }
      initializePromise = queryCurrentUser({ skipErrorHandler: true })
        .then((response) => {
          if (revision !== sessionRevision()) return;
          const user = normalizeCurrentUser(response.data);
          if (!user) clearLocalSession();
          set({
            currentUser: user,
            error: null,
            status: user ? 'authenticated' : 'anonymous',
          });
        })
        .catch((error: unknown) => {
          if (revision !== sessionRevision()) return;
          if (isUnauthorizedError(error)) {
            clearLocalSession();
            set({ currentUser: null, error: null, status: 'anonymous' });
            return;
          }
          set({
            currentUser: null,
            error: normalizeError(error),
            status: 'error',
          });
        })
        .finally(() => {
          initializePromise = undefined;
        });
    }

    return initializePromise;
  },
  login: async (params) => {
    cancelActiveLoginAttempt();
    resetSession();
    const attempt: LoginAttempt = {
      id: Symbol('login-attempt'),
      controller: new AbortController(),
    };
    activeLoginAttempt = attempt;
    try {
      const result = await submitVerifiedLogin(params, attempt.controller.signal);
      if (!isActiveLoginAttempt(attempt)) {
        return { status: 'stale' };
      }
      if (result.status !== 'ok') {
        return result;
      }

      if (result.token) {
        browserStorage.setItem('token', result.token);
      }
      const response = await queryCurrentUser({
        skipErrorHandler: true,
        signal: attempt.controller.signal,
      });
      if (!isActiveLoginAttempt(attempt)) {
        return { status: 'stale' };
      }
      const user = normalizeCurrentUser(response.data);
      if (!user) {
        throw new Error(response.errorMessage ?? '无法获取当前用户。');
      }
      set({ currentUser: user, error: null, status: 'authenticated' });
      return result;
    } catch (error) {
      if (!isActiveLoginAttempt(attempt)) {
        return { status: 'stale' };
      }
      resetSession();
      throw error;
    } finally {
      if (activeLoginAttempt?.id === attempt.id) {
        activeLoginAttempt = undefined;
      }
    }
  },
  cancelLogin: () => {
    if (activeLoginAttempt) {
      cancelActiveLoginAttempt();
      resetSession();
    }
  },
  logout: async () => {
    get().cancelLogin();
    const revision = sessionRevision();
    try {
      await outLogin({ skipErrorHandler: true });
    } finally {
      if (revision === sessionRevision()) {
        clearLocalSession();
        set({ currentUser: null, error: null, status: 'anonymous' });
      }
    }
  },
}));

onSessionReset(() => {
  cancelActiveLoginAttempt();
  useAuthStore.setState({ currentUser: null, error: null, status: 'anonymous' });
});
